import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaFlagCheckered, FaCalendarAlt, FaSearch, FaExchangeAlt } from 'react-icons/fa';
import './BuscadorViajes.css';

export default function BuscadorViajes({ onBuscar, mapaZonas }) {
  // 1. OBTENER ZONAS VALIDAS (Que sean Arrays reales)
  const zonasDisponibles = Object.keys(mapaZonas).filter(key => Array.isArray(mapaZonas[key]));

  // Estados
  const [tipoViaje, setTipoViaje] = useState('sencillo');
  const [zonaOrigen, setZonaOrigen] = useState('');
  const [terminalOrigen, setTerminalOrigen] = useState('');
  const [zonaDestino, setZonaDestino] = useState('');
  const [terminalDestino, setTerminalDestino] = useState('');
  const [fechaIda, setFechaIda] = useState('');
  const [fechaRegreso, setFechaRegreso] = useState('');

  // ✅ AUTO-SELECCIÓN: Si no hay nada seleccionado, elegir el primero automáticamente
  useEffect(() => {
    if (zonasDisponibles.length > 0) {
      if (!zonaOrigen) {
        const primeraZona = zonasDisponibles[0];
        setZonaOrigen(primeraZona);
        setTerminalOrigen(mapaZonas[primeraZona]?.[0] || '');

        const segundaZona = zonasDisponibles[1] || zonasDisponibles[0];
        setZonaDestino(segundaZona);
        setTerminalDestino(mapaZonas[segundaZona]?.[0] || '');
      }
    }
  }, [zonasDisponibles, mapaZonas, zonaOrigen]);

  // Lógica: Si cambio la ZONA Origen, actualizo su terminal
  useEffect(() => {
    if (zonaOrigen && mapaZonas[zonaOrigen] && !mapaZonas[zonaOrigen].includes(terminalOrigen)) {
      setTerminalOrigen(mapaZonas[zonaOrigen][0] || '');
    }
  }, [zonaOrigen, mapaZonas, terminalOrigen]);

  // Lógica: Si cambio la ZONA Destino, actualizo su terminal
  useEffect(() => {
    if (zonaDestino && mapaZonas[zonaDestino] && !mapaZonas[zonaDestino].includes(terminalDestino)) {
      setTerminalDestino(mapaZonas[zonaDestino][0] || '');
    }
  }, [zonaDestino, mapaZonas, terminalDestino]);

  // Intercambiar origen ↔ destino de un solo toque
  const intercambiarRuta = () => {
    const tOrig = terminalOrigen, zOrig = zonaOrigen;
    setTerminalOrigen(terminalDestino);
    setZonaOrigen(zonaDestino);
    setTerminalDestino(tOrig);
    setZonaDestino(zOrig);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!terminalOrigen || !terminalDestino) return alert("Espera a que carguen las rutas...");
    if (!fechaIda) return alert("Selecciona fecha de salida");

    // 🔒 CANDADO DE SEGURIDAD: Evita búsquedas nulas
    if (terminalOrigen === terminalDestino) {
      return alert("El origen y el destino no pueden ser el mismo lugar.");
    }

    if (tipoViaje === 'redondo' && !fechaRegreso) return alert("Selecciona fecha de regreso");

    onBuscar({
      origen: terminalOrigen,
      destino: terminalDestino,
      fecha: fechaIda,
      fechaRegreso: tipoViaje === 'redondo' ? fechaRegreso : null,
      tipoViaje,
      cantidadPasajeros: 1
    });
  };

  if (zonasDisponibles.length === 0) {
    return (
      <div className="bv-panel bv-panel--vacio">
        <h2>⚠️ No se encontraron rutas</h2>
        <p>Revisa la colección 'configuraciones/terminales' en Firebase.</p>
        <p className="bv-hint">Esperando Arrays...</p>
      </div>
    );
  }

  const renderOpciones = (excluirZona) =>
    zonasDisponibles
      .filter(z => z !== excluirZona)
      .map(zona => (
        <optgroup key={zona} label={zona.toUpperCase()}>
          {mapaZonas[zona].map(term => (
            <option key={term} value={term}>{term}</option>
          ))}
        </optgroup>
      ));

  return (
    <div className="bv-panel">
      <div className="bv-head">
        <span className="bv-kicker">Reserva en línea</span>
        <h2 className="bv-title">¿A dónde vas hoy?</h2>
      </div>

      {/* Control segmentado tipo de viaje */}
      <div className="bv-segment" data-active={tipoViaje}>
        <button type="button"
          className={tipoViaje === 'sencillo' ? 'is-active' : ''}
          onClick={() => setTipoViaje('sencillo')}>
          Solo ida
        </button>
        <button type="button"
          className={tipoViaje === 'redondo' ? 'is-active' : ''}
          onClick={() => setTipoViaje('redondo')}>
          Ida y vuelta
        </button>
        <span className="bv-segment__pill" />
      </div>

      <form onSubmit={handleSubmit} className="bv-form">

        {/* Bloque de ruta: origen — swap — destino */}
        <div className="bv-ruta">
          <div className="bv-campo">
            <label><FaMapMarkerAlt className="bv-ico bv-ico--origen" /> Salgo de</label>
            <select
              value={terminalOrigen}
              onChange={(e) => {
                const term = e.target.value;
                setTerminalOrigen(term);
                const zonaPadre = zonasDisponibles.find(z => mapaZonas[z].includes(term));
                if (zonaPadre) {
                  setZonaOrigen(zonaPadre);
                  if (zonaPadre === zonaDestino) {
                    const nuevaZonaDestino = zonasDisponibles.find(z => z !== zonaPadre);
                    if (nuevaZonaDestino) {
                      setZonaDestino(nuevaZonaDestino);
                      setTerminalDestino(mapaZonas[nuevaZonaDestino][0]);
                    }
                  }
                }
              }}
            >
              {renderOpciones(null)}
            </select>
          </div>

          <button type="button" className="bv-swap" onClick={intercambiarRuta} aria-label="Intercambiar origen y destino">
            <FaExchangeAlt />
          </button>

          <div className="bv-campo">
            <label><FaFlagCheckered className="bv-ico bv-ico--destino" /> Voy a</label>
            <select
              value={terminalDestino}
              onChange={(e) => {
                const term = e.target.value;
                setTerminalDestino(term);
                const zonaPadre = zonasDisponibles.find(z => mapaZonas[z].includes(term));
                if (zonaPadre) setZonaDestino(zonaPadre);
              }}
            >
              {renderOpciones(zonaOrigen)}
            </select>
          </div>
        </div>

        {/* Bloque de fechas */}
        <div className={`bv-fechas ${tipoViaje === 'redondo' ? 'es-redondo' : ''}`}>
          <div className="bv-campo">
            <label><FaCalendarAlt className="bv-ico" /> Fecha de salida</label>
            <input type="date" value={fechaIda}
              onChange={(e) => setFechaIda(e.target.value)}
              min={new Date().toLocaleDateString('en-CA')} required />
          </div>

          {tipoViaje === 'redondo' && (
            <div className="bv-campo bv-fadein">
              <label><FaCalendarAlt className="bv-ico" /> Fecha de regreso</label>
              <input type="date" value={fechaRegreso}
                onChange={(e) => setFechaRegreso(e.target.value)}
                min={fechaIda || new Date().toLocaleDateString('en-CA')} required />
            </div>
          )}
        </div>

        <button type="submit" className="bv-buscar">
          <FaSearch /> Buscar salidas
        </button>
      </form>
    </div>
  );
}
